<template>
  <div class="main-content-container overflow-hidden">
    <PageTitle :pageTitle="isEditing ? 'Edit Tutorial' : 'Add Tutorial'" subTitle="Management" />

    <div class="card bg-white border-0 rounded-3 mb-4">
      <div class="card-body p-4">
        <form @submit.prevent="submitForm">
          <div class="row">
            <div class="col-lg-12 col-md-12 mb-4">
              <label class="form-label text-dark fs-14 fw-medium">Title</label>
              <input type="text" class="form-control" v-model="form.title" placeholder="Enter tutorial title" required />
            </div>
            
            <div class="col-lg-6 col-md-6 mb-4">
              <label class="form-label text-dark fs-14 fw-medium">Service</label>
              <select class="form-select" v-model="form.service_id" required>
                <option value="" disabled>Select a service</option>
                <option v-for="service in services" :key="service.service_id" :value="service.service_id">
                  {{ service.service_name }}
                </option>
              </select>
            </div>
            
            <div class="col-lg-6 col-md-6 mb-4">
              <label class="form-label text-dark fs-14 fw-medium">Material Icon</label>
              <div class="d-flex align-items-center gap-2">
                <select class="form-select" v-model="form.icon">
                  <option value="">-- No Icon --</option>
                  <option value="plagiarism">Plagiarism</option>
                  <option value="edit_document">Edit Document</option>
                  <option value="auto_fix_high">Auto Fix High</option>
                  <option value="menu_book">Menu Book (Journal)</option>
                  <option value="translate">Translate</option>
                  <option value="school">School / Tutorial</option>
                  <option value="info">Info</option>
                  <option value="check_circle">Check Circle</option>
                  <option value="upload_file">Upload File</option>
                  <option value="contact_support">Contact Support</option>
                </select>
                <span v-if="form.icon" class="material-symbols-outlined fs-24 text-primary">{{ form.icon }}</span>
              </div>
            </div>

            <div class="col-lg-6 col-md-6 mb-4">
              <label class="form-label text-dark fs-14 fw-medium">Order</label>
              <input type="number" class="form-control" v-model="form.order" placeholder="0" min="0" required />
            </div>

            <div class="col-lg-6 col-md-6 mb-4 d-flex align-items-center">
              <div class="form-check form-switch mt-2">
                <input class="form-check-input" type="checkbox" id="isActive" v-model="form.is_active">
                <label class="form-check-label text-dark fs-14 fw-medium" for="isActive">Active (Visible to users)</label>
              </div>
            </div>

            <div class="col-lg-12 col-md-12 mb-4">
              <label class="form-label text-dark fs-14 fw-medium d-flex justify-content-between align-items-center">
                <span>Steps</span>
                <button type="button" class="btn btn-sm btn-outline-primary py-1 px-2" @click="addStep">
                  <i class="ri-add-line me-1"></i>Add Step
                </button>
              </label>
              <div v-for="(step, index) in form.steps" :key="index" class="d-flex mb-2 align-items-center gap-2">
                <span class="fw-bold fs-14 text-muted">{{ index + 1 }}.</span>
                <input type="text" class="form-control" v-model="form.steps[index]" placeholder="Enter step description" required />
                <button type="button" class="btn btn-outline-danger p-2 lh-1" @click="removeStep(index)" :disabled="form.steps.length <= 1">
                  <i class="material-symbols-outlined fs-18">delete</i>
                </button>
              </div>
            </div>
          </div>
          
          <div class="d-flex justify-content-end gap-2">
            <RouterLink to="/tutorials" class="btn btn-secondary fs-15 fw-medium">Cancel</RouterLink>
            <button type="submit" class="btn btn-primary fs-15 fw-medium" :disabled="loading">
              <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              {{ isEditing ? 'Update Tutorial' : 'Create Tutorial' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import PageTitle from '@/components/Common/PageTitle.vue';
import tutorialService from '@/services/tutorialService';
import serviceService from '@/services/serviceService';

export default defineComponent({
  name: 'TutorialFormPage',
  components: {
    PageTitle
  },
  setup() {
    const route = useRoute();
    const router = useRouter();
    
    const isEditing = ref(false);
    const tutorialId = ref<number | null>(null);
    const loading = ref(false);
    const services = ref<any[]>([]);
    
    const form = ref({
      title: '',
      service_id: '' as string | number,
      icon: '',
      order: 1,
      is_active: true,
      steps: [''] as string[]
    });

    onMounted(async () => {
      await fetchServices();
      if (route.params.id) {
        isEditing.value = true;
        tutorialId.value = Number(route.params.id);
        await fetchTutorial(tutorialId.value);
      }
    });

    const fetchServices = async () => {
      try {
        const response = await serviceService.getAll();
        services.value = response.data;
      } catch (error) {
        console.error('Error fetching services:', error);
      }
    };

    const fetchTutorial = async (id: number) => {
      try {
        const response = await tutorialService.getById(id);
        const data = response.data;
        form.value = {
          title: data.title,
          service_id: data.service_id,
          icon: data.icon || '',
          order: data.order || 1,
          is_active: !!data.is_active,
          steps: (data.steps && data.steps.length > 0) ? [...data.steps] : ['']
        };
      } catch (error) {
        console.error('Error fetching tutorial:', error);
        router.push('/tutorials');
      }
    };

    const addStep = () => {
      form.value.steps.push('');
    };

    const removeStep = (index: number) => {
      if (form.value.steps.length > 1) {
        form.value.steps.splice(index, 1);
      }
    };

    const submitForm = async () => {
      loading.value = true;
      try {
        const payload = {
          ...form.value,
          steps: form.value.steps.filter(s => s.trim() !== '')
        };

        if (isEditing.value && tutorialId.value) {
          await tutorialService.update(tutorialId.value, payload);
        } else {
          await tutorialService.create(payload);
        }
        router.push('/tutorials');
      } catch (error) {
        console.error('Error saving tutorial:', error);
      } finally {
        loading.value = false;
      }
    };

    return {
      isEditing,
      form,
      loading,
      services,
      addStep,
      removeStep,
      submitForm
    };
  }
});
</script>
